import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable, ErrorBox, Field, Panel, ScreenHeading, Tag } from '../../components/ui'
import { api, errorMessage } from '../../lib/api'
import { dateTime } from '../../lib/format'
import type { Catalogue } from '../../lib/types'
import { CatalogueEditor, EMPTY_CATALOGUE } from './CatalogueEditor'

export interface TenantSummary {
  id: string
  name: string
  slug: string
  currency: string
  locale: string
  active: boolean
  createdAt: string
  users: number
  quotes: number
  activeModel: { id: string; note: string; publishedAt: string | null } | null
  catalogue: Catalogue | null
  versions: number
}

interface Template {
  id: string
  name: string
  catalogue: Catalogue
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 31)

export function Tenants() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<TenantSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', slug: '', currency: 'EUR', locale: 'de-AT', adminEmail: '', adminName: '', adminPassword: '' })
  const [slugTouched, setSlugTouched] = useState(false)
  const [busy, setBusy] = useState(false)
  const [templates, setTemplates] = useState<Template[]>([])
  const [catalogue, setCatalogue] = useState<Catalogue>(EMPTY_CATALOGUE)
  const [scaffold, setScaffold] = useState(true)

  const reload = useCallback(() => api.get<TenantSummary[]>('/api/platform/tenants').then(setRows).catch((e) => setError(errorMessage(e))), [])
  useEffect(() => {
    reload()
    api
      .get<Template[]>('/api/platform/catalogue-templates')
      .then((tt) => {
        setTemplates(tt)
        // the standard drivers are the default for a new tenant
        const std = tt.find((x) => x.id === 'standard') ?? tt[0]
        if (std) setCatalogue((c) => (c.drivers.length ? c : (JSON.parse(JSON.stringify(std.catalogue)) as Catalogue)))
      })
      .catch(() => {})
  }, [reload])

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const t = await api.post<TenantSummary>('/api/platform/tenants', {
        name: form.name,
        slug: form.slug,
        currency: form.currency,
        locale: form.locale,
        admin: form.adminEmail ? { email: form.adminEmail, displayName: form.adminName || form.adminEmail, password: form.adminPassword || undefined } : undefined,
        catalogue: catalogue.drivers.length ? catalogue : null,
      })
      if (catalogue.drivers.length && scaffold) {
        await api.post(`/api/platform/tenants/${t.id}/scaffold-model`, { publish: false })
      }
      navigate(`/platform/tenants/${t.id}`)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <ScreenHeading title="Tenants" />
      {error && <div className="mb-4"><ErrorBox>{error}</ErrorBox></div>}
      <Panel className="mb-4">
        <DataTable
          rows={rows ?? []}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/platform/tenants/${r.id}`)}
          empty="No tenants yet."
          columns={[
            { key: 'n', header: 'Tenant', render: (r) => <span className="text-slate-200">{r.name}</span> },
            { key: 's', header: 'Handle', render: (r) => <span className="font-mono text-xs text-slate-400">{r.slug}</span> },
            { key: 'st', header: '', render: (r) => (r.active ? <Tag tone="pos">active</Tag> : <Tag tone="neg">inactive</Tag>) },
            { key: 'c', header: 'Currency · locale', render: (r) => <span className="text-xs text-slate-400">{r.currency} · {r.locale}</span> },
            { key: 'drv', header: 'Drivers', align: 'right', render: (r) => (r.catalogue ? r.catalogue.drivers.length : <Tag tone="warn">no catalogue</Tag>) },
            { key: 'u', header: 'Users', align: 'right', render: (r) => r.users },
            { key: 'q', header: 'Quotes', align: 'right', render: (r) => r.quotes },
            { key: 'm', header: 'Active model', render: (r) => (r.activeModel ? <span className="text-xs text-slate-400">{r.activeModel.id.slice(0, 8)} · {r.activeModel.note || '(no note)'}</span> : <Tag tone="warn">none</Tag>) },
            { key: 'd', header: 'Created', render: (r) => <span className="text-xs text-slate-500">{dateTime(r.createdAt)}</span> },
          ]}
        />
      </Panel>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="New tenant" subtitle="The handle is what users type at sign-in when their email exists in more than one tenant." className="lg:col-span-1">
          <div className="space-y-3">
            <Field label="Name">
              <input
                className="field"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: slugTouched ? form.slug : slugify(e.target.value) })}
                placeholder="Example Industries GmbH"
              />
            </Field>
            <Field label="Handle" hint="2–31 lowercase letters, digits or dashes">
              <input
                className="field font-mono"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true)
                  setForm({ ...form, slug: e.target.value })
                }}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Currency"><input className="field font-mono uppercase" maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></Field>
              <Field label="Locale"><input className="field font-mono" value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })} placeholder="de-AT" /></Field>
            </div>
            <div className="border-t border-slate-800 pt-3">
              <div className="label mb-2">First admin (optional)</div>
              <div className="space-y-3">
                <Field label="Email"><input className="field" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} /></Field>
                <Field label="Display name"><input className="field" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} /></Field>
                <Field label="Password (dev only; leave empty for SSO)"><input className="field" type="password" value={form.adminPassword} onChange={(e) => setForm({ ...form, adminPassword: e.target.value })} /></Field>
              </div>
            </div>
            <button type="button" className="btn btn-primary w-full" disabled={busy || !form.name || !form.slug} onClick={create}>
              {busy ? 'Creating…' : 'Create tenant'}
            </button>
          </div>
        </Panel>
        <div className="lg:col-span-2 rounded-lg border border-dashed border-slate-800 p-6 text-[14px] leading-relaxed text-slate-500">
          <div className="text-[15px] font-semibold text-slate-200">How a tenant goes live</div>
          <ol className="mt-3 list-decimal space-y-1.5 pl-5">
            <li>Create the tenant with its handle, currency and locale, and its first admin.</li>
            <li>Define the driver catalogue below: the blocks are fixed, the drivers and what their levels mean are the client's.</li>
            <li>Scaffold the first model version from the catalogue. Every weight starts at zero; the tenant admin enters weights or imports a calibrated version, then publishes.</li>
            <li>The tenant admin imports reference data and ratings; the sales force can quote once a version is active and every driver is rated.</li>
          </ol>
        </div>
      </div>
      <div className="mt-4">
        <Panel
          title="Driver catalogue"
          subtitle="Which drivers this tenant prices with, and what each level means. Weights are not part of it: they arrive with a model version, or start at zero in a scaffolded draft."
          right={
            <div className="flex items-center gap-2">
              {templates.map((t) => (
                <button key={t.id} type="button" className="btn !py-0.5 text-xs" title={t.name} onClick={() => setCatalogue(JSON.parse(JSON.stringify(t.catalogue)) as Catalogue)}>
                  Reset to {t.id} drivers
                </button>
              ))}
              <button type="button" className="btn !py-0.5 text-xs" onClick={() => setCatalogue(EMPTY_CATALOGUE)}>Clear</button>
            </div>
          }
        >
          <CatalogueEditor value={catalogue} onChange={setCatalogue} />
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={scaffold} onChange={(e) => setScaffold(e.target.checked)} />
            Also create the first model version as a draft with every weight at zero, for the tenant admin to fill in and publish
          </label>
        </Panel>
      </div>
    </div>
  )
}
